import React, { useState, useRef, useCallback } from "react";
import "./styles.css";

// Счётчик z-index: клик по окну поднимает его над остальными.
let zTop = 10;

const MIN_WIDTH = 260;
const MIN_HEIGHT = 220;

// Ручки ресайза: 4 стороны + 4 угла, каждая со своим курсором.
const RESIZE_HANDLES = [
    { dir: 'n', cursor: 'ns-resize' },
    { dir: 's', cursor: 'ns-resize' },
    { dir: 'e', cursor: 'ew-resize' },
    { dir: 'w', cursor: 'ew-resize' },
    { dir: 'ne', cursor: 'nesw-resize' },
    { dir: 'nw', cursor: 'nwse-resize' },
    { dir: 'se', cursor: 'nwse-resize' },
    { dir: 'sw', cursor: 'nesw-resize' },
];

// Перетаскиваемое и растягиваемое окно (как окно приложения на ПК): двигается за
// заголовок, тянется за края/углы, без затемняющего оверлея — можно открыть
// несколько и держать страницу активной. Закрытие — только по крестику.
//
// События — pointer, а не mouse: на телефоне мышиные события синтезируются лишь для тапа,
// и окно там не двигалось и не тянулось вовсе.
export default function Window({
    title, initialX = 96, initialY = 96, initialWidth = 420, initialHeight = 520,
    className = '', onClose, children,
}) {
    // Стартовый размер/позиция ужимаются под вьюпорт: на телефоне окно 400x720 в точке (96, 96)
    // наполовину уезжало за экран, а подвинуть его там нечем (тач-драг ниже, но окно уже за краем).
    const [rect, setRect] = useState(() => {
        const maxWidth = Math.max(MIN_WIDTH, window.innerWidth - 16);
        const maxHeight = Math.max(MIN_HEIGHT, window.innerHeight - 16);
        const width = Math.min(initialWidth, maxWidth);
        const height = Math.min(initialHeight, maxHeight);
        return {
            x: Math.max(8, Math.min(initialX, window.innerWidth - width - 8)),
            y: Math.max(0, Math.min(initialY, window.innerHeight - height - 8)),
            width,
            height,
        };
    });
    const [zIndex, setZIndex] = useState(() => ++zTop);
    const [cursor, setCursor] = useState(null); // курсор щита; null — взаимодействия нет (щита нет)
    const interactionRef = useRef(null);

    const bringToFront = useCallback(() => setZIndex(++zTop), []);

    const startMove = (event) => {
        if (event.target.closest('.window__close')) return; // клик по крестику — не тащим
        interactionRef.current = { mode: 'move', dir: null, startX: event.clientX, startY: event.clientY, origin: rect };
        setCursor('move');
    };

    const startResize = (dir, cur) => (event) => {
        interactionRef.current = { mode: 'resize', dir, startX: event.clientX, startY: event.clientY, origin: rect };
        setCursor(cur);
    };

    const handlePointerMove = (event) => {
        const it = interactionRef.current;
        if (!it) return;
        const dx = event.clientX - it.startX;
        const dy = event.clientY - it.startY;
        const o = it.origin;

        if (it.mode === 'move') {
            setRect({ ...o, x: o.x + dx, y: Math.max(0, o.y + dy) }); // заголовок не уводим за верх
            return;
        }

        let { x, y, width, height } = o;
        const dir = it.dir;
        if (dir.includes('e')) width = Math.max(MIN_WIDTH, o.width + dx);
        if (dir.includes('s')) height = Math.max(MIN_HEIGHT, o.height + dy);
        if (dir.includes('w')) {
            const right = o.x + o.width;                       // правый край на месте
            x = Math.min(o.x + dx, right - MIN_WIDTH);
            width = right - x;
        }
        if (dir.includes('n')) {
            const bottom = o.y + o.height;                     // нижний край на месте
            y = Math.min(Math.max(0, o.y + dy), bottom - MIN_HEIGHT);
            height = bottom - y;
        }
        setRect({ x, y, width, height });
    };

    const endInteraction = () => {
        interactionRef.current = null;
        setCursor(null);
    };

    return <>
        {cursor
            // Щит поверх всего на время перетаскивания/ресайза: держит курсор и
            // перехватывает мышь, чтобы события не «проваливались» в iframe.
            ? <div
                className="window__shield"
                style={{ cursor }}
                onPointerMove={handlePointerMove}
                onPointerUp={endInteraction}
                onPointerCancel={endInteraction}
                onMouseLeave={endInteraction}
            />
            : null}
        <div
            className={`window ${className}`}
            style={{ left: rect.x, top: rect.y, width: rect.width, height: rect.height, zIndex }}
            onPointerDown={bringToFront}
        >
            <div className="window__header" onPointerDown={startMove}>
                <div className="window__title">{title}</div>
                <button type="button" className="window__close" title="Закрыть" onClick={onClose}>×</button>
            </div>
            <div className="window__body">{children}</div>
            {RESIZE_HANDLES.map((handle) => (
                <div
                    key={handle.dir}
                    className={`window__resize window__resize_${handle.dir}`}
                    onPointerDown={startResize(handle.dir, handle.cursor)}
                />
            ))}
        </div>
    </>;
}
